import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Divider,
  Typography,
  InputBase,
  styled,
  alpha,
  IconButton,
  Skeleton,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import debounce from 'lodash/debounce';

// Constants
const SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 2;
const MAX_RESULTS_PER_CATEGORY = 5;
const API_TIMEOUT = 20000;

// Styled components
const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: 25,
  backgroundColor: theme.palette.grey[100],
  "&:hover": {
    backgroundColor: theme.palette.grey[200],
  },
  display: "flex",
  alignItems: "center",
  height: 50,
  width: 300,
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    maxWidth: 200,
  },
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  height: 36,
  width: 46,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  backgroundColor: "#f5c518",
  marginLeft: theme.spacing(1),
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  fontSize: 14,
  width: "100%",
  height: "100%",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 2),
    width: "100%",
  },
}));

const ClearButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(0.5),
  top: '50%',
  transform: 'translateY(-50%)',
  padding: theme.spacing(0.5),
  color: 'inherit',
}));

// Custom hook for API calls
const useSearchData = (accessToken, baseURL) => {
  const [data, setData] = useState({
    sites: [],
    groups: [],
    devices: [],
    siteMap: new Map(),
    groupMap: new Map(),
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!accessToken) return;
    
    setLoading(true);
    setError(null);
    
    const axiosConfig = {
      headers: { Authorization: `Bearer ${accessToken}` },
      timeout: API_TIMEOUT,
    };

    try {
      const sitesRes = await axios.get(`${baseURL}/get-sites`, axiosConfig);
      const sites = sitesRes.data.data || [];
      
      if (sites.length === 0) {
        setData({
          sites: [],
          groups: [],
          devices: [],
          siteMap: new Map(),
          groupMap: new Map(),
        });
        return;
      }

      const siteMap = new Map(sites.map(site => [site.id, site.name]));

      const fetchPromises = sites.map(async (site) => {
        const [groupsRes, devicesRes] = await Promise.allSettled([
          axios.get(`${baseURL}/groups`, {
            ...axiosConfig,
            params: { site_id: site.id },
          }),
          axios.get(`${baseURL}/devices`, {
            ...axiosConfig,
            params: { site_id: site.id },
          }),
        ]);

        return {
          siteId: site.id,
          groups: groupsRes.status === 'fulfilled' ? groupsRes.value.data || [] : [],
          devices: devicesRes.status === 'fulfilled' ? devicesRes.value.data || [] : [],
        };
      });

      const results = await Promise.all(fetchPromises);

      const groupsMap = new Map();
      const devicesMap = new Map();

      results.forEach(({ groups, devices }) => {
        groups.forEach(group => groupsMap.set(group.id, group));
        devices.forEach(device => devicesMap.set(device.id, device));
      });

      const allGroups = Array.from(groupsMap.values());
      const allDevices = Array.from(devicesMap.values());
      const groupMap = new Map(allGroups.map(group => [group.id, group.name]));

      setData({
        sites,
        groups: allGroups,
        devices: allDevices,
        siteMap,
        groupMap,
      });
    } catch (error) {
      console.error('Search data fetch error:', error);
      setError('Failed to load search data. Please try refreshing the page.');
    } finally {
      setLoading(false);
    }
  }, [accessToken, baseURL]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
};

// Search utility functions
const createSearchMatcher = (query) => {
  const queryLower = query.toLowerCase().trim();
  const queryWords = queryLower.split(/\s+/).filter(word => word.length > 0);
  
  return (text) => {
    const textLower = String(text).toLowerCase();
    return queryWords.every(word => textLower.includes(word));
  };
};

const scoreMatch = (item, fields, query) => {
  const queryLower = query.toLowerCase();
  let score = 0;
  
  fields.forEach(field => {
    const value = String(item[field]).toLowerCase();
    if (value === queryLower) score += 100;
    else if (value.startsWith(queryLower)) score += 50;
    else if (value.includes(queryLower)) score += 10;
  });
  
  return score;
};

const SearchBar = ({ setOpen }) => {
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({
    sites: [],
    groups: [],
    devices: [],
  });
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const inputRef = useRef(null);
  const searchAbortController = useRef(null);

  const baseURL = process.env.REACT_APP_BASEURL;
  const { data, loading: dataLoading, error: dataError, refetch } = useSearchData(accessToken, baseURL);

  // Initialize search query from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const query = params.get('q') || '';
    setSearchQuery(query);
  }, [location.search]);

  // Create stable debounced search function
  const debouncedSearch = useRef(
    debounce((query, searchData) => {
      if (searchAbortController.current) {
        searchAbortController.current.abort();
      }
      searchAbortController.current = new AbortController();

      if (query.length < MIN_SEARCH_LENGTH) {
        setSearchResults({ sites: [], groups: [], devices: [] });
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      
      try {
        const matcher = createSearchMatcher(query);
        
        const filterAndScore = (items, fields) => {
          return items
            .filter(item => fields.some(field => matcher(item[field])))
            .map(item => ({ ...item, _score: scoreMatch(item, fields, query) }))
            .sort((a, b) => b._score - a._score)
            .slice(0, MAX_RESULTS_PER_CATEGORY);
        };

        const filteredSites = filterAndScore(searchData.sites, ['name', 'id']);
        const filteredGroups = filterAndScore(searchData.groups, ['name', 'id']);
        const filteredDevices = filterAndScore(searchData.devices, ['name', 'id', 'device_type']);

        setSearchResults({
          sites: filteredSites,
          groups: filteredGroups,
          devices: filteredDevices,
        });
      } catch (error) {
        if (!searchAbortController.current?.signal.aborted) {
          console.error('Search error:', error);
        }
      } finally {
        setIsSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS)
  );

  // Perform search when query or data changes
  useEffect(() => {
    debouncedSearch.current(searchQuery, data);
    
    return () => {
      if (searchAbortController.current) {
        searchAbortController.current.abort();
      }
    };
  }, [searchQuery, data]);

  // Handle input change
  const handleInputChange = useCallback((e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setShowDropdown(value.length >= MIN_SEARCH_LENGTH);
  }, []);

  // Handle result click
  const handleResultClick = useCallback((type, id, name, item = {}) => {
    setShowDropdown(false);
    
    // Save to recent searches
    const newSearch = { query: searchQuery, type, id, name, item, timestamp: Date.now() };
    setRecentSearches(prev => {
      const filtered = prev.filter(prevItem => 
        !(prevItem.type === type && prevItem.id === id)
      ).slice(0, 4);
      return [newSearch, ...filtered];
    });

    const encodedQuery = encodeURIComponent(searchQuery);
    const refreshKey = Math.random().toString(36).substring(2, 8);
    let state = {};
    switch (type) {
      case 'sites':
        state = { siteId: id };
        break;
      case 'groups':
        state = { siteId: item.site, groupId: id };
        break;
      case 'devices':
        state = { siteId: item.site, deviceId: id };
        break;
      default:
        break;
    }
    navigate(`/dashboard/statistics?q=${encodedQuery}&refresh=${refreshKey}`, { state });
  }, [searchQuery, navigate]);

  // Handle clear
  const handleClear = useCallback(() => {
    setSearchQuery('');
    setSearchResults({ sites: [], groups: [], devices: [] });
    setShowDropdown(false);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  }, []);

  // Handle dropdown close
  const handleClose = useCallback(() => {
    setShowDropdown(false);
  }, []);

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      handleClose();
    }
  }, [handleClose]);

  const open = showDropdown;
  const hasResults = Object.values(searchResults).some(results => results.length > 0);
  const showRecentSearches = searchQuery.length < MIN_SEARCH_LENGTH && recentSearches.length > 0;

  // Loading skeleton
  const LoadingSkeleton = () => (
    <>
      {[...Array(3)].map((_, i) => (
        <ListItem key={i}>
          <Skeleton variant="text" width="80%" />
        </ListItem>
      ))}
    </>
  );

  // Results section component
  const ResultsSection = ({ title, results, type }) => (
    <>
      <ListSubheader>{title}</ListSubheader>
      {results.length > 0 ? (
        results.map((item) => (
          <ListItem
            key={`${type}-${item.id}`}
            component="button"
            onClick={() => handleResultClick(type, item.id, item.name, item)}
            sx={{
              pl: 4,
              backgroundColor: "transparent",
              border: "0px",
              "&:hover": { backgroundColor: 'grey.200' },
            }}
          >
            <ListItemText
              primary={
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2">
                    {item.name}
                  </Typography>
                  <Chip
                    label={`ID: ${item.id}`}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                </Box>
              }
              secondary={
                type === 'groups'
                  ? `Site: ${data.siteMap.get(item.site) || item.site}`
                  : type === 'devices'
                  ? `Site: ${data.siteMap.get(item.site) || item.site}${
                      item.groups?.length > 0
                        ? `, Group: ${data.groupMap.get(item.groups[0]) || item.groups[0]}`
                        : ''
                    }`
                  : undefined
              }
            />
          </ListItem>
        ))
      ) : (
        <ListItem sx={{ pl: 4 }}>
          <ListItemText
            primary={`No ${title.toLowerCase()} found for "${searchQuery}"`}
            primaryTypographyProps={{ color: 'text.secondary', variant: 'body2' }}
          />
        </ListItem>
      )}
      <Divider />
    </>
  );

  return (
    <Box sx={{ position: 'relative' }}>
      <Search>
        <SearchIconWrapper>
          <SearchIcon />
        </SearchIconWrapper>
        <StyledInputBase
          ref={inputRef}
          placeholder="Search"
          inputProps={{ 'aria-label': 'Search' }}
          value={searchQuery}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => searchQuery.length >= MIN_SEARCH_LENGTH && setShowDropdown(true)}
        />
        {searchQuery && (
          <ClearButton
            size="small"
            onClick={handleClear}
            aria-label="Clear search"
          >
            <ClearIcon fontSize="small" />
          </ClearButton>
        )}
      </Search>

      {open && (hasResults || showRecentSearches || dataError || isSearching) && (
        <Box
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1300,
            bgcolor: 'background.paper',
            boxShadow: 3,
            borderRadius: 1,
            mt: 0.5,
            maxHeight: '70vh',
            overflowY: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            minWidth: { xs: '280px', sm: '400px' },
          }}
        >
          <List>
            {dataError && (
              <ListItem>
                <ListItemText
                  primary={
                    <Typography color="error" variant="body2">
                      {dataError}
                    </Typography>
                  }
                  secondary={
                    <Typography
                      variant="caption"
                      color="primary"
                      sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                      onClick={refetch}
                    >
                      Retry
                    </Typography>
                  }
                />
              </ListItem>
            )}

            {isSearching && !dataError && <LoadingSkeleton />}

            {showRecentSearches && (
              <>
                <ListSubheader>Recent Searches</ListSubheader>
                {recentSearches.map((search, index) => (
                  <ListItem
                    key={index}
                    component="button"
                    onClick={() => {
                      setSearchQuery(search.query);
                      handleResultClick(search.type, search.id, search.name, search.item || {});
                    }}
                    sx={{ pl: 4 }}
                  >
                    <ListItemText
                      primary={search.name}
                      secondary={`${search.type} • ${search.query}`}
                    />
                  </ListItem>
                ))}
                <Divider />
              </>
            )}

            {!isSearching && !dataError && hasResults && (
              <>
                <ResultsSection title="Sites" results={searchResults.sites} type="sites" />
                <ResultsSection title="Groups" results={searchResults.groups} type="groups" />
                <ResultsSection title="Devices" results={searchResults.devices} type="devices" />
              </>
            )}
          </List>
        </Box>
      )}
    </Box>
  );
};

export default SearchBar;